import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { notificationService, AppNotification } from '../services/notificationService';
import { useAuth } from '../hooks/useAuth';

const NotificationsPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [selectedNotification, setSelectedNotification] = useState<AppNotification | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchNotifications();
  }, [user, page, filter]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const data = await notificationService.getNotifications({
        page,
        per_page: 20,
        unread_only: filter === 'unread'
      });
      if (page === 1) {
        setNotifications(data.notifications);
      } else {
        setNotifications(prev => [...prev, ...data.notifications]);
      }
      setTotalPages(data.pages);
      setUnreadCount(data.unread_count);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId: number) => {
    try {
      await notificationService.markAsRead(notificationId);
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
      if (selectedNotification?.id === notificationId) {
        setSelectedNotification({ ...selectedNotification, is_read: true });
      }
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'report':
        return '⚠️';
      case 'comment':
        return '💬';
      case 'comment_reply':
        return '↩️';
      case 'comment_like':
        return '❤️';
      case 'mention':
        return '👤';
      case 'post_update':
        return '📝';
      case 'post_like':
        return '👍';
      case 'post_approved':
        return '✅';
      case 'post_rejected':
        return '❌';
      case 'post_pinned':
        return '📌';
      case 'message':
        return '💌';
      default:
        return '🔔';
    }
  };

  const getNotificationTypeLabel = (type: string) => {
    switch (type) {
      case 'report':
        return 'Báo cáo';
      case 'comment':
        return 'Bình luận';
      case 'comment_reply':
        return 'Trả lời';
      case 'comment_like':
        return 'Thích bình luận';
      case 'mention':
        return 'Nhắc đến';
      case 'post_update':
        return 'Cập nhật';
      case 'post_like':
        return 'Thích bài viết';
      case 'post_approved':
        return 'Đã duyệt';
      case 'post_rejected':
        return 'Từ chối';
      case 'post_pinned':
        return 'Ghim';
      case 'message':
        return 'Tin nhắn';
      default:
        return 'Thông báo';
    }
  };

  // Real-time current time for accurate time display
  const [currentTime, setCurrentTime] = useState(() => {
    const now = new Date();
    const utcTime = now.getTime() + (now.getTimezoneOffset() * 60 * 1000);
    const vietnamTime = utcTime + (7 * 60 * 60 * 1000);
    return vietnamTime;
  });

  // Update current time every 1 minute for real-time display
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const utcTime = now.getTime() + (now.getTimezoneOffset() * 60 * 1000);
      const vietnamTime = utcTime + (7 * 60 * 60 * 1000);
      setCurrentTime(vietnamTime);
    };

    // Update immediately
    updateTime();

    // Then update every 1 minute (60000ms) for accurate time display
    const timeInterval = setInterval(updateTime, 60000);

    return () => clearInterval(timeInterval);
  }, []);

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    // Convert to Vietnam timezone (UTC+7)
    const utcTime = date.getTime();
    const vietnamTime = utcTime + (7 * 60 * 60 * 1000);
    
    // Calculate difference using current time
    const diffMs = currentTime - vietnamTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Vừa xong';
    if (diffMins < 60) return `${diffMins} phút trước`;
    if (diffHours < 24) return `${diffHours} giờ trước`;
    if (diffDays < 7) return `${diffDays} ngày trước`;
    return date.toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Ho_Chi_Minh'
    });
  };

  const handleNotificationClick = (notification: AppNotification) => {
    // If notification is for a message, navigate directly to chat
    if (notification.type === 'message' && notification.related_user_id) {
      if (notification.related_user && notification.related_user.username) {
        navigate(`/chat/${notification.related_user.username}`);
      } else {
        navigate('/chat');
      }
      if (!notification.is_read) {
        handleMarkAsRead(notification.id);
      }
      return;
    }
    
    setSelectedNotification(notification);
    setShowDetailModal(true);
    if (!notification.is_read) {
      handleMarkAsRead(notification.id);
    }
  };

  const handleNavigateToPost = (notification: AppNotification) => {
    if (notification.related_comment_id && notification.related_post_id) {
      navigate(`/community#post-${notification.related_post_id}&comment-${notification.related_comment_id}`);
    } else if (notification.related_post_id) {
      navigate(`/community#post-${notification.related_post_id}`);
    }
    setShowDetailModal(false);
  };

  const handleNavigateToChat = (notification: AppNotification) => {
    if (notification.related_user && notification.related_user.username) {
      navigate(`/chat/${notification.related_user.username}`);
    } else {
      navigate('/chat');
    }
    setShowDetailModal(false);
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-gray-900">Thông báo</h1>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 bg-blue-50 rounded-lg transition"
              >
                Đánh dấu tất cả đã đọc
              </button>
            )}
          </div>

          {/* Filters */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => {
                setFilter('all');
                setPage(1);
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                filter === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Tất cả
            </button>
            <button
              onClick={() => {
                setFilter('unread');
                setPage(1);
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                filter === 'unread'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Chưa đọc {unreadCount > 0 && `(${unreadCount})`}
            </button>
          </div>
        </div>

        {/* Notifications List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {loading && page === 1 ? (
            <div className="p-12 text-center text-gray-500">Đang tải...</div>
          ) : notifications.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <div className="text-4xl mb-4">🔔</div>
              <p className="text-lg font-medium">
                {filter === 'unread' ? 'Không có thông báo chưa đọc' : 'Không có thông báo nào'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-6 hover:bg-gray-50 transition cursor-pointer ${
                    !notification.is_read ? 'bg-blue-50' : ''
                  }`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex items-start gap-4">
                    <div className="text-3xl flex-shrink-0">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4 mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className={`text-base font-semibold ${!notification.is_read ? 'text-gray-900' : 'text-gray-700'}`}>
                              {notification.title}
                            </p>
                            {!notification.is_read && (
                              <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                            <span className="px-2 py-0.5 bg-gray-100 rounded-full">
                              {getNotificationTypeLabel(notification.type)}
                            </span>
                            <span>•</span>
                            <span>{formatTime(notification.created_at)}</span>
                          </div>
                          {notification.content && (
                            <p className="text-sm text-gray-600 line-clamp-2">
                              {notification.content}
                            </p>
                          )}
                        </div>
                      </div>
                      {(notification.related_post || notification.related_user) && (
                        <div className="flex items-center gap-4 mt-3">
                          {notification.related_post && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleNavigateToPost(notification);
                              }}
                              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                            >
                              Xem bài viết →
                            </button>
                          )}
                          {notification.related_user && (
                            <Link
                              to={`/user/${notification.related_user.id}`}
                              onClick={(e) => e.stopPropagation()}
                              className="text-sm text-gray-600 hover:text-gray-700"
                            >
                              {notification.related_user.full_name || notification.related_user.username}
                            </Link>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Load More */}
          {!loading && page < totalPages && (
            <div className="p-6 text-center border-t border-gray-100">
              <button
                onClick={() => setPage(prev => prev + 1)}
                className="px-6 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 bg-blue-50 rounded-lg transition"
              >
                Tải thêm
              </button>
            </div>
          )}

          {loading && page > 1 && (
            <div className="p-6 text-center text-gray-500">Đang tải...</div>
          )}
        </div>
      </div>

      {/* Notification Detail Modal */}
      {showDetailModal && selectedNotification && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => setShowDetailModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 border-b border-gray-200 flex items-start justify-between">
              <div className="flex items-start gap-4 flex-1">
                <div className="text-4xl flex-shrink-0">
                  {getNotificationIcon(selectedNotification.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-xl font-bold text-gray-900 mb-2">
                    {selectedNotification.title}
                  </h2>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="px-2 py-1 bg-gray-100 rounded-full text-xs font-medium text-gray-700">
                      {getNotificationTypeLabel(selectedNotification.type)}
                    </span>
                    <span className="text-sm text-gray-500">{formatTime(selectedNotification.created_at)}</span>
                    {!selectedNotification.is_read && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                        Chưa đọc
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-600 transition p-1"
                aria-label="Đóng"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto flex-1">
              {selectedNotification.content ? (
                <div className="prose prose-sm max-w-none">
                  <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                    {selectedNotification.content}
                  </p>
                </div>
              ) : (
                <p className="text-gray-500 italic">Không có nội dung chi tiết</p>
              )}

              {/* Related Information */}
              {selectedNotification.type === 'message' && selectedNotification.related_user && (
                <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-700 mb-1">
                        Tin nhắn từ:
                      </p>
                      <p className="text-sm text-gray-600">
                        {selectedNotification.related_user.full_name || selectedNotification.related_user.username}
                      </p>
                    </div>
                    <button
                      onClick={() => handleNavigateToChat(selectedNotification)}
                      className="ml-4 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition whitespace-nowrap"
                    >
                      Mở cuộc trò chuyện →
                    </button>
                  </div>
                </div>
              )}

              {selectedNotification.related_post && (
                <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-700 mb-1">
                        {selectedNotification.related_comment_id ? 'Bình luận liên quan:' : 'Bài viết liên quan:'}
                      </p>
                      <p className="text-sm text-gray-600">{selectedNotification.related_post.title}</p>
                    </div>
                    <button
                      onClick={() => handleNavigateToPost(selectedNotification)}
                      className="ml-4 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition whitespace-nowrap"
                    >
                      {selectedNotification.related_comment_id ? 'Xem bình luận →' : 'Xem bài viết →'}
                    </button>
                  </div>
                </div>
              )}

              {/* Show related user only if not a message notification (message notifications already show user above) */}
              {selectedNotification.related_user && selectedNotification.type !== 'message' && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-sm font-medium text-gray-700 mb-1">Người dùng liên quan:</p>
                  <Link
                    to={`/user/${selectedNotification.related_user.id}`}
                    className="text-sm text-blue-600 hover:text-blue-700"
                  >
                    {selectedNotification.related_user.full_name || selectedNotification.related_user.username}
                  </Link>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 flex items-center justify-between bg-gray-50">
              <div className="flex items-center gap-2">
                {!selectedNotification.is_read && (
                  <button
                    onClick={async () => {
                      await handleMarkAsRead(selectedNotification.id);
                    }}
                    className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition"
                  >
                    Đánh dấu đã đọc
                  </button>
                )}
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300 transition"
              >
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsPage;

